import {
    escapePrintText,
    formatPrintDate,
    formatPrintMoney,
} from "@/shared/lib/brand-document-print-a4";
import type { BrandPrintLanguage } from "@/shared/lib/brand-document-print-language";
import {
    getInstallmentPlanPrintI18n,
    getPerformanceDocumentPrintI18n,
} from "@/shared/lib/performance-brand-print-i18n";
import type { DocumentCurrency } from "@/shared/lib/performance-document-currency";

export const PERFORMANCE_PAYMENT_BANK = {
  bankName: "BCEL",
  accountName: "Fanglao Studio",
  accountNumber: "—",
} as const;

const PAYMENT_METHOD_LABELS = {
  cash: "ເງິນສົດ",
  transfer: "ໂອນເງິນ",
  card: "ບັດ",
} as const;

export type PrintInstallmentRow = {
  installmentNo: number;
  label: string | null;
  dueDate: Date | string | null;
  amount: number;
  status: "pending" | "paid" | "overdue" | "cancelled";
};

export type InstallmentPrintTaxContext = {
  grandTotal: number;
  taxAmount: number;
  taxEnabled?: boolean;
};

export type InstallmentPlanDocumentAmounts = {
  subtotal: number;
  billDiscountAmount?: number | null;
  taxEnabled?: boolean | null;
  taxAmount?: number;
  grandTotal: number;
};

export function documentNetBeforeTax(
  doc: Pick<InstallmentPlanDocumentAmounts, "subtotal" | "billDiscountAmount">,
): number {
  return Math.max(0, doc.subtotal - (doc.billDiscountAmount ?? 0));
}

/** ยอดเป้าหมายที่แผนงวดต้องรวม — ไม่รวม VAT เมื่อเปิดภาษี */
export function installmentPlanTargetAmount(
  doc: InstallmentPlanDocumentAmounts,
): number {
  if (doc.taxEnabled || (doc.taxAmount ?? 0) > 0) {
    return documentNetBeforeTax(doc);
  }
  return doc.grandTotal;
}

/** แผนเก่าที่บันทึกยอดรวม VAT ไว้ */
export function isLegacyTaxInclusiveInstallmentPlan(
  planSum: number,
  doc: InstallmentPlanDocumentAmounts,
): boolean {
  if (!doc.taxEnabled && (doc.taxAmount ?? 0) <= 0) return false;
  const target = installmentPlanTargetAmount(doc);
  return (
    Math.abs(planSum - doc.grandTotal) <= 1 &&
    Math.abs(planSum - target) > 1
  );
}

/** แปลงยอดงวดในแผน (ex-tax) → invoice grandTotal */
export function installmentPlanAmountToDocumentGrandTotal(
  doc: InstallmentPlanDocumentAmounts,
  installmentAmount: number,
): number {
  const target = installmentPlanTargetAmount(doc);
  if (target <= 0) return installmentAmount;
  if (!doc.taxEnabled && (doc.taxAmount ?? 0) <= 0) {
    return installmentAmount;
  }
  return Math.round((installmentAmount * doc.grandTotal) / target);
}

/** ยอดที่ลูกค้าจ่ายจริงตื่องวด (รวม VAT เมื่อเปิดภาษี — ยกเว้นแผน legacy ที่เก็บยอดรวม VAT อยู่แล้ว) */
export function resolveInstallmentPaymentAmount(
  doc: InstallmentPlanDocumentAmounts,
  installmentAmount: number,
  options?: { planSum?: number },
): number {
  const planSum = options?.planSum ?? installmentAmount;
  if (isLegacyTaxInclusiveInstallmentPlan(planSum, doc)) {
    return installmentAmount;
  }
  return installmentPlanAmountToDocumentGrandTotal(doc, installmentAmount);
}

/** ยอดงวด/รายการบน print — ไม่รวม VAT (แยกแสดงใน summary) */
export function resolveInstallmentPrintAmountExTax(
  rowAmount: number,
  taxContext?: InstallmentPrintTaxContext,
): number {
  if (!taxContext?.taxEnabled && (taxContext?.taxAmount ?? 0) <= 0) {
    return rowAmount;
  }
  const grandTotal = taxContext?.grandTotal ?? 0;
  const taxAmount = taxContext?.taxAmount ?? 0;
  if (grandTotal <= 0 || taxAmount <= 0) return rowAmount;
  const taxShare = Math.round((rowAmount * taxAmount) / grandTotal);
  return rowAmount - taxShare;
}

/** ยอดแสดงในรายการ — ex-tax โดยไม่หัก VAT ซ้ำเมื่อแผนใหม่เก็บยอดก่อนภาษีแล้ว */
export function resolveInstallmentLineAmountForDisplay(
  rowAmount: number,
  doc: InstallmentPlanDocumentAmounts,
  planSum: number,
): number {
  if (!doc.taxEnabled && (doc.taxAmount ?? 0) <= 0) {
    return rowAmount;
  }
  if (!isLegacyTaxInclusiveInstallmentPlan(planSum, doc)) {
    return rowAmount;
  }
  return resolveInstallmentPrintAmountExTax(rowAmount, {
    grandTotal: doc.grandTotal,
    taxAmount: doc.taxAmount ?? 0,
    taxEnabled: doc.taxEnabled ?? false,
  });
}

function looksLikeHtml(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Convert legacy plain-text terms (one line = one item) into editor HTML. */
export function toPaymentTermsEditorHtml(
  content: string | null | undefined,
): string {
  const raw = content ?? "";
  if (!raw.trim()) return "<p></p>";
  if (looksLikeHtml(raw)) return raw;
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return "<p></p>";
  return `<ol>${lines
    .map((line) => `<li><p>${escapeHtmlText(line)}</p></li>`)
    .join("")}</ol>`;
}

export function paymentTermsHtmlToNullable(html: string): string | null {
  const visible = html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return visible.length ? html.trim() : null;
}

export function countPaymentTermsItems(
  content: string | null | undefined,
): number {
  const raw = content ?? "";
  if (!raw.trim()) return 0;
  if (looksLikeHtml(raw)) {
    const li = raw.match(/<li\b/gi);
    if (li?.length) return li.length;
    return paymentTermsHtmlToNullable(raw) ? 1 : 0;
  }
  return parsePaymentTermsLines(raw).length;
}

function sanitizePaymentTermsHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript:/gi, "");
}

function renderTermsList(items: string[]): string {
  return `<ol class="bd-payment-terms-list">${items
    .map((item) => `<li>${escapePrintText(item)}</li>`)
    .join("")}</ol>`;
}

function parsePaymentTermsLines(content: string | null | undefined): string[] {
  if (!content?.trim()) return [];
  return content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export function renderDocumentPaymentTermsHtml(options: {
  content: string | null | undefined;
  title: string;
}): string {
  const raw = options.content?.trim() ?? "";
  if (!raw) return "";

  if (looksLikeHtml(raw)) {
    const body = sanitizePaymentTermsHtml(raw);
    if (!paymentTermsHtmlToNullable(body)) return "";
    return `
    <section class="bd-notes bd-payment-terms">
      <p class="bd-notes-title">${escapePrintText(options.title)}</p>
      <div class="bd-payment-terms-body">${body}</div>
    </section>
  `;
  }

  const items = parsePaymentTermsLines(raw);
  if (items.length === 0) return "";

  return `
    <section class="bd-notes bd-payment-terms">
      <p class="bd-notes-title">${escapePrintText(options.title)}</p>
      ${renderTermsList(items)}
    </section>
  `;
}

export function renderDocumentBankAccountHtml(options: {
  content: string | null | undefined;
  title: string;
}): string {
  const raw = options.content?.trim() ?? "";
  if (!raw) return "";

  if (looksLikeHtml(raw)) {
    const body = sanitizePaymentTermsHtml(raw);
    if (!paymentTermsHtmlToNullable(body)) return "";
    return `
    <section class="bd-bank-account">
      <p class="bd-notes-title">${escapePrintText(options.title)}</p>
      <div class="bd-bank-account-body">${body}</div>
    </section>
  `;
  }

  const items = parsePaymentTermsLines(raw);
  if (items.length === 0) return "";

  return `
    <section class="bd-bank-account">
      <p class="bd-notes-title">${escapePrintText(options.title)}</p>
      ${renderTermsList(items)}
    </section>
  `;
}

/** @deprecated Use document.paymentTerms + renderDocumentPaymentTermsHtml */
export function renderQuotationPaymentTermsHtml(options?: {
  validUntil?: Date | string | null;
}): string {
  const validUntilText = options?.validUntil
    ? formatPrintDate(options.validUntil)
    : null;

  const items = [
    validUntilText
      ? `ໃບສະເໜີລາຄານີ້ມີຜົນຈົນວັນທີ ${validUntilText}`
      : "ໃບສະເໜີລາຄານີ້ມີຜົນພາຍໃນ 30 ວັນ ນັບຈາກວັນອອກເອກະສານ",
    "ລາຄາແລະແຜນຊຳລະທີ່ລະບຸໃນໃບສະເໜີລາຄາເປັນຂໍ້ສະເໜີ ແລະ ຈະຜູກພັນເມື່ອອອກໃບເກັບເງິນ",
    "ຊຳລະໄດ້ໂດຍເງິນສົດ, ໂອນເງິນ ຫຼື ບັດ",
    `ບັນຊີຮັບເງິນ: ${PERFORMANCE_PAYMENT_BANK.accountName} — ${PERFORMANCE_PAYMENT_BANK.bankName} ${PERFORMANCE_PAYMENT_BANK.accountNumber}`,
    "ການຈອງວັນສະແດງ ແລະ ການຈັດທີມຈະດຳເນີນການຫຼັງຮັບມັດຈຳ ຫຼື ຕາມທີ່ຕົກລົງໃນສັນຍາ",
  ];

  return renderDocumentPaymentTermsHtml({
    content: items.join("\n"),
    title: "ເງື່ອນໄຂການຊຳລະເງິນ (ຂໍ້ສະເໜີ)",
  });
}

/** @deprecated Use document.paymentTerms + renderDocumentPaymentTermsHtml */
export function renderInvoicePaymentTermsHtml(): string {
  const items = [
    "ໃບເກັບເງິນນີ້ເປັນຂໍ້ຕົກລົງການຊຳລະທີ່ຜູກພັນຕໍ່ຍອດສັນຍາ",
    "ຊຳລະໄດ້ເປັນຫຼາຍງວດຕາມແຜນງວດດ້ານລຸ່ມ (ຖ້າມີ)",
    "ຊຳລະໄດ້ໂດຍເງິນສົດ, ໂອນເງິນ ຫຼື ບັດ",
    `ບັນຊີຮັບເງິນ: ${PERFORMANCE_PAYMENT_BANK.accountName} — ${PERFORMANCE_PAYMENT_BANK.bankName} ${PERFORMANCE_PAYMENT_BANK.accountNumber}`,
    "ກະລຸນາອ້າງອີງເລກທີໃບເກັບເງິນເມື່ອໂອນເງິນ",
    "ງວດທີ່ເກີນກຳນົດ ອາດມີຄ່າປັບຕາມທີ່ຕົກລົງ",
  ];

  return renderDocumentPaymentTermsHtml({
    content: items.join("\n"),
    title: "ເງື່ອນໄຂການຊຳລະເງິນ",
  });
}

export function renderInstallmentPlanPrintHtml(
  installments: PrintInstallmentRow[],
  lang: BrandPrintLanguage = "lo",
  currency: DocumentCurrency = "LAK",
  options?: {
    planDocument?: InstallmentPlanDocumentAmounts;
    planSum?: number;
    taxContext?: InstallmentPrintTaxContext;
  },
): string {
  if (installments.length === 0) return "";

  const i18n = getInstallmentPlanPrintI18n(lang);
  const planSum =
    options?.planSum ??
    installments.reduce((sum, row) => sum + row.amount, 0);

  const rows = installments
    .map((row) => {
      const label =
        row.label?.trim() || i18n.defaultLabel(row.installmentNo);
      const displayAmount = options?.planDocument
        ? resolveInstallmentLineAmountForDisplay(
            row.amount,
            options.planDocument,
            planSum,
          )
        : resolveInstallmentPrintAmountExTax(row.amount, options?.taxContext);
      return `
        <tr>
          <td class="num">${row.installmentNo}</td>
          <td>${escapePrintText(label)}</td>
          <td class="num">${escapePrintText(formatPrintDate(row.dueDate, lang))}</td>
          <td class="num">${formatPrintMoney(displayAmount, lang, currency)}</td>
        </tr>`;
    })
    .join("");

  return `
    <section class="bd-installment-plan">
      <p class="bd-notes-title">${escapePrintText(i18n.title)}</p>
      <table class="bd-table bd-installment-table">
        <thead>
          <tr>
            <th class="num" style="width: 14mm;">${escapePrintText(i18n.columns.no)}</th>
            <th>${escapePrintText(i18n.columns.detail)}</th>
            <th style="width: 28mm;">${escapePrintText(i18n.columns.due)}</th>
            <th class="num" style="width: 32mm;">${escapePrintText(i18n.columns.amount)}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </section>
  `;
}

/** ตารางรายการงวดหลักบน invoice/receipt (แทนรายการจาก quotation) */
export function renderDocumentInstallmentLinesTableHtml(
  installments: PrintInstallmentRow[],
  lang: BrandPrintLanguage = "lo",
  currency: DocumentCurrency = "LAK",
  options?: {
    /** exTax = ยอดก่อนภาษี (รายงาน) · inclusive = ยอดลูกค้าจ่าย (รวม VAT เมื่อเปิดภาษี) */
    amountMode?: "exTax" | "inclusive";
    taxContext?: InstallmentPrintTaxContext;
    planDocument?: InstallmentPlanDocumentAmounts;
    planSum?: number;
  },
): string {
  if (installments.length === 0) return "";

  const i18n = getInstallmentPlanPrintI18n(lang);
  const docI18n = getPerformanceDocumentPrintI18n(lang);
  const amountMode = options?.amountMode ?? "inclusive";
  const planSum =
    options?.planSum ??
    installments.reduce((sum, row) => sum + row.amount, 0);

  const rows = installments
    .map((row, index) => {
      const label =
        row.label?.trim() || i18n.defaultLabel(row.installmentNo);
      const displayAmount =
        amountMode === "exTax"
          ? options?.planDocument
            ? resolveInstallmentLineAmountForDisplay(
                row.amount,
                options.planDocument,
                planSum,
              )
            : resolveInstallmentPrintAmountExTax(
                row.amount,
                options?.taxContext,
              )
          : options?.planDocument
            ? resolveInstallmentPaymentAmount(
                options.planDocument,
                row.amount,
                { planSum },
              )
            : row.amount;
      return `
        <tr>
          <td>${index + 1}</td>
          <td>${escapePrintText(label)}</td>
          <td class="num">1</td>
          <td class="num">${formatPrintMoney(displayAmount, lang, currency)}</td>
        </tr>`;
    })
    .join("");

  return `
    <table class="bd-table">
      <thead>
        <tr>
          <th style="width: 10mm;">#</th>
          <th>${escapePrintText(i18n.columns.detail)}</th>
          <th class="num" style="width: 16mm;">${escapePrintText(docI18n.table.quantity)}</th>
          <th class="num" style="width: 32mm;">${escapePrintText(i18n.columns.amount)}</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

export type DocumentPrintSummaryInput = {
  subtotal: number;
  discountAmount?: number;
  billDiscountAmount?: number;
  taxEnabled?: boolean;
  taxRate?: number;
  taxAmount?: number;
  grandTotal: number;
  /** QT แสดง breakdown ส่วนลด — INV/RCPT ซ่อนส่วนลด แต่ยังแสดงภาษีถ้ามี */
  showDiscountBreakdown?: boolean;
  /** งวดแรกของใบเสนอราคา เมื่อแบ่งจ่ายมากกว่า 1 งวด */
  firstDeposit?: { percent: number; amount: number } | null;
};

export function resolveQuotationFirstDepositSummary(
  installments: Array<{
    installmentNo: number;
    amount: number;
    status?: string;
  }>,
  doc: InstallmentPlanDocumentAmounts,
): { percent: number; amount: number } | null {
  const active = installments
    .filter((row) => row.status !== "cancelled" && row.amount > 0)
    .sort((a, b) => a.installmentNo - b.installmentNo);
  if (active.length < 2) return null;

  const planSum = active.reduce((sum, row) => sum + row.amount, 0);
  const first = active[0];
  if (!first || planSum <= 0) return null;

  const percent = Math.round((first.amount / planSum) * 100);
  if (percent <= 0) return null;

  return {
    percent,
    amount: resolveInstallmentPaymentAmount(doc, first.amount, { planSum }),
  };
}

export function buildDocumentPrintSummaryHtml(
  input: DocumentPrintSummaryInput,
  lang: BrandPrintLanguage = "lo",
  currency: DocumentCurrency = "LAK",
): string {
  const i18n = getPerformanceDocumentPrintI18n(lang);
  const showDiscountBreakdown = input.showDiscountBreakdown !== false;
  const lineDiscount = Math.max(0, input.discountAmount ?? 0);
  const billDiscount = Math.max(0, input.billDiscountAmount ?? 0);
  const taxAmount = input.taxAmount ?? 0;
  const hasLineDiscount = showDiscountBreakdown && lineDiscount > 0;
  const hasBillDiscount = showDiscountBreakdown && billDiscount > 0;
  const hasTax = Boolean(input.taxEnabled) || taxAmount !== 0;
  const showSubtotalRow =
    (showDiscountBreakdown &&
      (hasLineDiscount || hasBillDiscount || hasTax)) ||
    (!showDiscountBreakdown && hasTax);
  const grossSubtotal = input.subtotal + lineDiscount;

  const rows: string[] = [];

  if (hasLineDiscount) {
    rows.push(
      `<div class="bd-summary-row"><span>${escapePrintText(i18n.summary.grossSubtotal)}</span><strong>${formatPrintMoney(grossSubtotal, lang, currency)}</strong></div>`,
    );
    rows.push(
      `<div class="bd-summary-row"><span>${escapePrintText(i18n.summary.discount)}</span><strong>-${formatPrintMoney(lineDiscount, lang, currency)}</strong></div>`,
    );
  }

  if (showSubtotalRow) {
    rows.push(
      `<div class="bd-summary-row"><span>${escapePrintText(i18n.summary.subtotal)}</span><strong>${formatPrintMoney(input.subtotal, lang, currency)}</strong></div>`,
    );
  }

  if (hasBillDiscount) {
    rows.push(
      `<div class="bd-summary-row"><span>${escapePrintText(i18n.summary.billDiscount)}</span><strong>-${formatPrintMoney(billDiscount, lang, currency)}</strong></div>`,
    );
  }

  if (hasTax) {
    rows.push(
      `<div class="bd-summary-row"><span>${escapePrintText(input.taxEnabled ? i18n.summary.taxWithRate(input.taxRate ?? 0) : i18n.summary.tax)}</span><strong>${formatPrintMoney(taxAmount, lang, currency)}</strong></div>`,
    );
  }

  rows.push(
    `<div class="bd-summary-row grand"><span>${escapePrintText(i18n.summary.grandTotal)}</span><span>${formatPrintMoney(input.grandTotal, lang, currency)}</span></div>`,
  );

  if (input.firstDeposit && input.firstDeposit.amount > 0) {
    rows.push(
      `<div class="bd-summary-row"><span>${escapePrintText(i18n.summary.deposit(input.firstDeposit.percent))}</span><strong>${formatPrintMoney(input.firstDeposit.amount, lang, currency)}</strong></div>`,
    );
  }

  return `<section class="bd-summary">${rows.join("")}</section>`;
}

export function renderDocumentInstallmentSummaryHtml(
  options: {
    grandTotal: number;
    subtotal?: number;
    discountAmount?: number;
    billDiscountAmount?: number;
    taxEnabled?: boolean;
    taxRate?: number;
    taxAmount?: number;
  },
  lang: BrandPrintLanguage = "lo",
  currency: DocumentCurrency = "LAK",
): string {
  return buildDocumentPrintSummaryHtml(
    {
      subtotal: options.subtotal ?? 0,
      discountAmount: options.discountAmount ?? 0,
      billDiscountAmount: options.billDiscountAmount ?? 0,
      taxEnabled: options.taxEnabled,
      taxRate: options.taxRate,
      taxAmount: options.taxAmount,
      grandTotal: options.grandTotal,
    },
    lang,
    currency,
  );
}

export function renderReceiptPaymentSummaryHtml(
  options: {
  paymentMethod?: "cash" | "transfer" | "card" | null;
  installmentLabel?: string | null;
  invoiceGrandTotal: number;
  receiptAmount: number;
  paidAmountAfter: number;
  remainingAfter: number;
  },
  lang: BrandPrintLanguage = "lo",
): string {
  const labels = getPerformanceDocumentPrintI18n(lang).receiptPayment;
  const paidBefore = Math.max(
    options.paidAmountAfter - options.receiptAmount,
    0,
  );

  const metaItems: string[] = [];

  if (options.paymentMethod) {
    metaItems.push(`
      <div class="bd-receipt-payment-meta-item">
        <span>${escapePrintText(labels.paymentMethod)}</span>
        <strong>${escapePrintText(labels.methods[options.paymentMethod])}</strong>
      </div>`);
  }

  if (options.installmentLabel) {
    metaItems.push(`
      <div class="bd-receipt-payment-meta-item">
        <span>${escapePrintText(labels.installment)}</span>
        <strong>${escapePrintText(options.installmentLabel)}</strong>
      </div>`);
  }

  return `
    <section class="bd-receipt-payment">
      <p class="bd-notes-title">${escapePrintText(labels.title)}</p>
      ${
        metaItems.length > 0
          ? `<div class="bd-receipt-payment-meta">${metaItems.join("")}</div>`
          : ""
      }
      <div class="bd-summary bd-receipt-payment-summary">
        <div class="bd-summary-row">
          <span>${escapePrintText(labels.invoiceTotal)}</span>
          <strong>${formatPrintMoney(options.invoiceGrandTotal, lang)}</strong>
        </div>
        <div class="bd-summary-row">
          <span>${escapePrintText(labels.paidBefore)}</span>
          <strong>${formatPrintMoney(paidBefore, lang)}</strong>
        </div>
        <div class="bd-summary-row bd-summary-row--receipt-current">
          <span>${escapePrintText(labels.paidThisTime)}</span>
          <strong>${formatPrintMoney(options.receiptAmount, lang)}</strong>
        </div>
        <div class="bd-summary-row">
          <span>${escapePrintText(labels.paidCumulative)}</span>
          <strong>${formatPrintMoney(options.paidAmountAfter, lang)}</strong>
        </div>
        <div class="bd-summary-row grand">
          <span>${escapePrintText(labels.remaining)}</span>
          <span>${formatPrintMoney(options.remainingAfter, lang)}</span>
        </div>
      </div>
    </section>
  `;
}
